import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import { signal, computed, batch } from '@preact/signals'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import {
    ToolPage,
    Disclaimer,
    ToolWorkbench,
} from '../../components/tool-page.js'
import {
    calcSection608Risk,
    QUESTIONS,
    DIMENSIONS,
    type ExposureLevel,
    type Section608RiskResult,
} from '../../util/section-608-risk.js'
import { exportPdf, type PdfSection } from '../../util/pdf.js'
import { EM_DASH } from '../../constants.js'

const STORAGE_KEY = 'hvaclog-section-608-risk-answers'

const EXPOSURE_LABELS:Record<ExposureLevel, string> = {
    low: 'Low Exposure ' + EM_DASH + ' practices appear generally sound.',
    moderate: 'Moderate Exposure ' + EM_DASH + ' some gaps identified; improvement recommended.',
    high: 'High Exposure ' + EM_DASH + ' significant compliance gaps present.',
    critical: 'Critical Exposure ' + EM_DASH + ' major violations or systemic gaps detected.',
}

function loadSavedAnswers ():Record<string, number> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return {}
        return JSON.parse(raw) as Record<string, number>
    } catch {
        return {}
    }
}

function saveAnswers (answers:Record<string, number>):void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(answers))
    } catch {
        // storage not available
    }
}

const answers = signal<Record<string, number>>(loadSavedAnswers())
const submitted = signal(false)

const result = computed(():Section608RiskResult | null => {
    if (!submitted.value) return null
    return calcSection608Risk(answers.value)
})

function handleAnswer (questionId:string, score:number):void {
    const next = { ...answers.value, [questionId]: score }
    answers.value = next
    saveAnswers(next)
}

function handleSubmit (e:Event):void {
    e.preventDefault()
    submitted.value = true
}

function handleReset ():void {
    batch(() => {
        answers.value = {}
        submitted.value = false
    })
    saveAnswers({})
}

function handleDownloadPdf (e:Event):void {
    e.preventDefault()
    const r = result.value
    if (!r) return

    const sections:PdfSection[] = []

    sections.push({
        heading: 'Exposure Level',
        rows: [
            {
                label: 'Level',
                value: r.exposureLevel.toUpperCase(),
            },
            {
                label: 'Score',
                value: `${r.totalScore.toFixed(1)} / 100`,
            },
            {
                label: 'Summary',
                value: EXPOSURE_LABELS[r.exposureLevel],
            },
        ],
    })

    sections.push({
        heading: 'Dimension Scores',
        rows: r.dimensionScores.map(d => ({
            label: d.label,
            value: `${d.score.toFixed(0)}% (weighted: ${
                d.weightedScore.toFixed(1)
            })`,
        })),
    })

    if (r.recommendations.length > 0) {
        sections.push({
            heading: 'Recommendations',
            rows: r.recommendations.map((rec, i) => ({
                label: String(i + 1),
                value: rec,
            })),
        })
    }

    exportPdf({
        title: 'EPA Section 608 Risk Estimator Report',
        sections,
        footer:
            'For educational and operational planning purposes only. ' +
            'Not legal advice. Does not predict enforcement outcomes.',
    })
}

function answeredCount ():number {
    return QUESTIONS.filter(
        q => answers.value[q.id] !== undefined
    ).length
}

export const Section608RiskEstimator:FunctionComponent<{
    state:AppState
}> = function Section608RiskEstimator () {
    const r = result.value
    const answered = answeredCount()

    return html`
        <${ToolPage}
            title="EPA Section 608 Risk Estimator"
            description="Answer 10 questions across 5 compliance dimensions to
                estimate your organization's EPA fine-exposure level."
        >
            <${ToolWorkbench}
                inputsLabel="Questionnaire"
                resultsLabel="Risk estimate"
                emptyHint="Answer the questions, then select Estimate Risk."
            >
                <form
                class="tool-form"
                onSubmit=${handleSubmit}
            >
                ${DIMENSIONS.map(dim => html`
                    <div class="tool-form-section" key=${dim.id}>
                        <p class="tool-section-label">${dim.label}</p>
                        ${QUESTIONS.filter(q =>
                            q.dimension === dim.id
                        ).map(q => html`
                            <div class="tool-field" key=${q.id}>
                                <p class="tool-field-label">${q.text}</p>
                                <div
                                    class="tool-choices"
                                    role="radiogroup"
                                    aria-label=${q.text}
                                >
                                    ${q.options.map(opt => html`
                                        <label
                                            class="tool-choice"
                                            key=${opt.score}
                                        >
                                            <input
                                                type="radio"
                                                name=${q.id}
                                                value=${opt.score}
                                                checked=${
                                                    answers.value[q.id]
                                                        === opt.score
                                                }
                                                onChange=${() =>
                                                    handleAnswer(
                                                        q.id,
                                                        opt.score
                                                    )
                                                }
                                            />
                                            ${opt.label}
                                        </label>
                                    `)}
                                </div>
                            </div>
                        `)}
                    </div>
                `)}

                <div class="tool-actions">
                    <substrate-button class="primary" type="submit">
                        Estimate Risk${answered < QUESTIONS.length ?
                            ` (${answered}/${QUESTIONS.length} answered)` :
                            ''
                        }
                    </substrate-button>
                    <substrate-button
                        type="button"
                        onClick=${(e:Event) => {
                            e.preventDefault()
                            handleReset()
                        }}
                    >
                        Reset
                    </substrate-button>
                </div>
            </form>

            ${r && html`
                <div class="tool-readout">
                    <span
                        class="tool-badge"
                        data-level=${r.exposureLevel}
                    >
                        ${r.exposureLevel} exposure
                    </span>

                    <p class="tool-readout-detail">
                        Score: ${r.totalScore.toFixed(1)} / 100
                    </p>
                    <p class="tool-readout-detail">
                        ${EXPOSURE_LABELS[r.exposureLevel]}
                    </p>

                    <div class="tool-metrics">
                        ${r.dimensionScores.map(d => html`
                            <div class="tool-metric" key=${d.id}>
                                <p class="tool-metric-label">${d.label}</p>
                                <p class="tool-metric-value">
                                    ${d.score.toFixed(0)}%
                                </p>
                            </div>
                        `)}
                    </div>

                    ${r.recommendations.length > 0 && html`
                        <div class="tool-recs">
                            <h3>Recommendations</h3>
                            <ol>
                                ${r.recommendations.map((rec, i) => html`
                                    <li key=${i}>${rec}</li>
                                `)}
                            </ol>
                        </div>
                    `}

                    <div class="tool-actions">
                        <substrate-button
                            type="button"
                            onClick=${handleDownloadPdf}
                        >
                            Download PDF Report
                        </substrate-button>
                    </div>
                </div>
            `}
            </${ToolWorkbench}>

            <${Disclaimer} />
        </${ToolPage}>
    `
}
